import { useState, Dispatch, SetStateAction } from 'react'

/**
 * Returns whether a value has changed since the last render.
 *
 * Returns `false` on mount. Uses reference equality (`!==`).
 *
 * This implements React's "adjusting state during render" pattern — use the returned
 * boolean in a conditional to set state without effects.
 *
 * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 *
 * @example
 * if (useHasChanged(items) && canReset) {
 *   setSelection(null)
 * }
 */
export function useHasChanged<T>(value: T): boolean {
  const [prev, setPrev] = useState(value)
  if (value !== prev) {
    setPrev(value)
    return true
  }
  return false
}

/**
 * Returns whether any value in the array has changed since the last render.
 *
 * Returns `false` on mount. Uses reference equality (`!==`) per element,
 * consistent with React's dependency array comparison.
 *
 * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 *
 * @example
 * if (useHasAnyChanged([filters, searchTerm, page])) {
 *   setSelectedItem(null)
 * }
 */
export function useHasAnyChanged(values: unknown[]): boolean {
  const [prev, setPrev] = useState(values)
  const changed = values.length !== prev.length || values.some((v, i) => v !== prev[i])
  if (changed) {
    setPrev(values)
    return true
  }
  return false
}

/**
 * Creates state that stays in sync with a prop, but can be overridden locally.
 *
 * Useful for optimistic updates or local edits where you want state to:
 * 1. Initialize from a prop
 * 2. Update when the prop changes (e.g., after refetch)
 * 3. Be updatable locally (e.g., optimistic UI)
 *
 * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 *
 * @example
 * // Instead of:
 * const [isPinned, setIsPinned] = useState(task.is_pinned)
 * useEffect(() => {
 *   setIsPinned(task.is_pinned)
 * }, [task.is_pinned])
 *
 * // Use:
 * const [isPinned, setIsPinned] = useSyncedState(task.is_pinned)
 *
 * // State syncs with prop, but can still be updated optimistically:
 * const { mutateAsync } = useMutation({
 *   onMutate: () => setIsPinned(prev => !prev),
 *   onError: () => setIsPinned(prev => !prev),
 * })
 *
 * @param propValue - The prop value to sync with
 * @returns A tuple of [state, setState] that stays in sync with propValue
 */
export function useSyncedState<T>(propValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState(propValue)
  const [prevProp, setPrevProp] = useState(propValue)
  if (propValue !== prevProp) {
    setPrevProp(propValue)
    setValue(propValue)
  }
  return [value, setValue]
}

/**
 * Creates state that resets to an initial value when dependencies change.
 *
 * Useful for resetting form state or UI state when a modal opens, when switching
 * between items, or when key props change.
 *
 * Consider using the `key` prop pattern instead if:
 * - You want to reset ALL state in a component
 * - The component is used like: `<Form key={item.id} item={item} />`
 *
 * @see https://react.dev/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes
 *
 * @example
 * // Instead of:
 * const [isChoosing, setIsChoosing] = useState(false)
 * useEffect(() => {
 *   setIsChoosing(context?.can_cancel ?? false)
 * }, [isOpen, context?.can_cancel])
 *
 * // Use:
 * const [isChoosing, setIsChoosing] = useResetState(
 *   context?.can_cancel ?? false,
 *   [isOpen, context?.can_cancel]
 * )
 *
 * @param initialValue - The value to reset to when deps change
 * @param deps - Dependency array — state resets when any value changes (uses reference equality, like React's built-in hooks)
 * @returns A tuple of [state, setState] that resets when deps change
 */
export function useResetState<T>(initialValue: T, deps: unknown[]): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState(initialValue)
  const [prevDeps, setPrevDeps] = useState(deps)

  const depsChanged = deps.length !== prevDeps.length || deps.some((dep, i) => dep !== prevDeps[i])
  if (depsChanged) {
    setPrevDeps(deps)
    setValue(initialValue)
  }

  return [value, setValue]
}
